import { MenuItemType } from '../components/MenuItemType';
import {
    getGroupIdFromTableQuery,
    isGroupTableQuery,
    lazyIsGroupOwner,
    lazyIsPrivateUnjoinedGroup,
    isMessageActionFromGroup,
} from 'owa-group-utils';
import { composeStore } from 'owa-mail-compose-store';
import { isDumpsterSearchTable } from 'owa-mail-list-search';
import {
    isDumpsterTable,
    listViewStore,
    MailRowDataPropertyGetter,
    TableView,
} from 'owa-mail-list-store';
import { isBulkActionRunning } from 'owa-bulk-action-store';
import getGroupFolderAndRulePermission from 'owa-groups-shared-store/lib/selectors/getGroupFolderAndRulePermission';
import { isFeatureEnabled } from 'owa-feature-flags';

export default function getMailMenuItemShouldDisable(menuItem: MenuItemType): boolean {
    const tableView = listViewStore.tableViews.get(listViewStore.selectedTableViewId);

    switch (menuItem) {
        case MenuItemType.NewMessage:
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (26,41): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
            // @ts-expect-error
            return isNewMessageDisabled(tableView);
        case MenuItemType.Delete:
            return !!tableView && isDeleteDisabled(tableView);
        case MenuItemType.Move:
            return !!tableView && isMoveDisabled(tableView);
        case MenuItemType.Copy:
            return !!tableView && isCopyDisabled(tableView);
        default:
            return false;
    }
}

// Show new message as disabled
// - when we in a dumpster table
// - when we are in a private unjoined group
// - when a new message is still in the process of being created
function isNewMessageDisabled(tableView: TableView): boolean {
    const tableQuery = tableView.tableQuery;
    const isDumpsterOrDumpsterSearchTable =
        isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery);

    if (isDumpsterOrDumpsterSearchTable) {
        return true;
    }

    if (isGroupTableQuery(tableQuery)) {
        const isPrivateUnjoinedGroup = lazyIsPrivateUnjoinedGroup.tryImportForRender();
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (61,9): Type 'boolean | undefined' is not assignable to type 'boolean'.
        // @ts-expect-error
        return isPrivateUnjoinedGroup?.(getGroupIdFromTableQuery(tableQuery));
    }

    if (composeStore.newMessageCreationInProgress) {
        return true;
    }

    return false;
}

// Show delete as disabled
// - if tableview is in selectAll mode and a bulk action is currently already running
// - when we are in a group, we are not the group owner, AND
// --- we have multiple rows selected OR
// --- we do not have canDelete permissions on the message
function isDeleteDisabled(tableView: TableView): boolean {
    const tableQuery = tableView.tableQuery;

    if (isBulkActionRunning(tableView.serverFolderId) && tableView.isInVirtualSelectAllMode) {
        return true;
    }

    if (isGroupTableQuery(tableQuery)) {
        const folderRulePermission = getGroupFolderAndRulePermission(
            getGroupIdFromTableQuery(tableView.tableQuery)
        );

        const isGroupOwner = lazyIsGroupOwner.tryImportForRender();
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (88,29): Argument of type 'string | null' is not assignable to parameter of type 'string'.
        // @ts-expect-error
        if (!isGroupOwner?.(getGroupIdFromTableQuery(tableQuery))) {
            const selectedKeys = [...tableView.selectedRowKeys.keys()];
            if (
                selectedKeys.length !== 1 ||
                !(
                    MailRowDataPropertyGetter.getCanDelete(selectedKeys[0], tableView) ||
                    (isFeatureEnabled('grp-loadFolders') &&
                        !!folderRulePermission?.canDeleteMessage)
                )
            ) {
                return true;
            }
        }
    }

    return false;
}

// Show Move as disabled
// - if action is invoked inside a group, AND permissions are not allowed
function isMoveDisabled(tableView: TableView): boolean {
    if (isMessageActionFromGroup()) {
        const folderRulePermission = getGroupFolderAndRulePermission(
            getGroupIdFromTableQuery(tableView.tableQuery)
        );
        return !folderRulePermission?.canMoveMessage;
    }

    return false;
}

// Show Copy as disabled
// - if action is invoked inside a group, AND permissions are not allowed
function isCopyDisabled(tableView: TableView): boolean {
    if (isMessageActionFromGroup()) {
        const folderRulePermission = getGroupFolderAndRulePermission(
            getGroupIdFromTableQuery(tableView.tableQuery)
        );
        return !folderRulePermission?.canCopyMessage;
    }

    return false;
}
